import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import RefreshCheckApi from '../../communication_system/axios_apis/refresh_check/refresh_check_api';
import { Wrapper02 } from '../../designComponents/wrappers/FlexMainWrapper';
import MainPageTitle from '../../designComponents/titles/MainPageTitle';
import SearchbarWithDropdown from '../../designComponents/searchbars/SearchbarWithDropdown';
import MainTable from '../../designComponents/tables/MainTable';
import { DefaultModal } from '../../designComponents/modals/ModalComponent';
import { dateFilter, moneyFilter } from '../../utils/textFilter';
import { membershipAdminLinks, membershipSearchOptions, membershipStateLabel, membershipStateOptions } from './membershipPresentation';
import UserInfoModalContext from '../../data_system/contexts/user_context/user_info_modal_context';
import UserModal from '../../components/UserComponents/UserModal';
import PayInfoModal from '../allPayment/PayInfoModal';
import { getPaymentDetail } from '../allPayment/allPaymentUtils';

const methodLabel = {
    0: '카드',
    1: '무통장 입금',
    2: '카카오페이',
    3: '네이버페이',
    4: '계좌이체',
    8: '해외카드',
};

const paymentStatusLabel = {
    0: '결제 대기',
    1: '입금 대기',
    2: '결제 완료',
    3: '환불 신청',
    4: '환불 완료',
    5: '환불 거절',
    6: '입금 기한 만료',
    8: '취소',
    9: '처리 중',
};

const Membership = () => {
    const { user_info_modal_state, user_info_modal_actions } = useContext(UserInfoModalContext);
    const tableRef = useRef();
    const [searchText, setSearchText] = useState(() => new URLSearchParams(window.location.search).get('search') || '');
    const [searchType, setSearchType] = useState('phone');
    const [statuses, setStatuses] = useState([]);
    const [order, setOrder] = useState('desc');
    const [selected, setSelected] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const lastResponseRef = useRef(null);
    const param = useMemo(() => ({ search: searchText, searchType, status: statuses, order }), [searchText, searchType, statuses, order]);

    const requestParams = (page) => ({
        page,
        size: 10,
        search: searchText || undefined,
        search_type: searchType,
        status: statuses,
        order,
    });

    const load = async (page) => {
        try {
            const response = await RefreshCheckApi.get('/adminMembership', { params: requestParams(page) });
            lastResponseRef.current = { page, param, data: response.data };
            return response.data.memberships || [];
        } catch (error) {
            console.error(error);
            lastResponseRef.current = { page, param, data: { memberships: [], all_count: 0 } };
            return [];
        }
    };

    const count = async (page) => {
        if (lastResponseRef.current?.page === page && lastResponseRef.current?.param === param) {
            return lastResponseRef.current.data.all_count;
        }
        try {
            const response = await RefreshCheckApi.get('/adminMembership', { params: requestParams(page) });
            return response.data.all_count;
        } catch (error) {
            console.error(error);
            return 0;
        }
    };

    const toggleStatus = (status) => {
        setStatuses((current) => (current.includes(status) ? current.filter((item) => item !== status) : [...current, status]));
    };

    const stopRowClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const openPayment = async (event, payId) => {
        stopRowClick(event);
        try {
            const response = await RefreshCheckApi.get(`/adminSyrup/payments/detail?pay_id=${payId}`);
            const detail = getPaymentDetail(response);
            if (!detail) throw new Error('결제 상세 정보가 없습니다');
            setSelectedPayment({ data: detail, origin: detail });
        } catch (error) {
            console.error(error);
            alert('결제 상세 정보를 불러오지 못했습니다.');
        }
    };

    const openUser = (event, userId) => {
        stopRowClick(event);
        user_info_modal_actions.setId(userId);
        user_info_modal_actions.setModalVisible(true);
    };

    return (
        <Wrapper02>
            {user_info_modal_state.modalVisible && <UserModal />}
            <MainPageTitle text={'멤버십'} onClick={() => tableRef.current?.refresh()} />
            <SearchbarWithDropdown
                list={membershipSearchOptions}
                resetThenSet={useCallback((item) => setSearchType(item.value), [])}
                searchText={searchText}
                onChangeInput={(event) => setSearchText(searchType === 'phone' ? event.target.value.replaceAll('-', '') : event.target.value)}
                onChangeSubmit={(event) => {
                    event.preventDefault();
                    tableRef.current?.refresh();
                }}
            />
            <div style={{ display: 'flex', gap: 16, margin: '16px 0' }}>
                {membershipStateOptions.map(([value, label]) => (
                    <label key={value}>
                        <input type="checkbox" checked={statuses.includes(value)} onChange={() => toggleStatus(value)} /> {label}
                    </label>
                ))}
                <span style={{ borderLeft: '1px solid #ddd', marginLeft: 8, paddingLeft: 16 }}>시작일 정렬</span>
                {[
                    ['desc', '내림차순'],
                    ['asc', '오름차순'],
                ].map(([value, label]) => (
                    <label key={value}>
                        <input type="radio" name="membership-order" checked={order === value} onChange={() => setOrder(value)} /> {label}
                    </label>
                ))}
            </div>
            <MainTable
                ref={tableRef}
                param={param}
                setParam={() => {}}
                toggleBlur
                checkAble={false}
                loadingText={'멤버십 데이터를 불러오고 있습니다.'}
                noneDataText={'조회 결과가 없어요'}
                callApi={load}
                pageCountCallApi={count}
                onClickRowItem={(item) => () => setSelected(item)}
                headerData={[
                    { key: 'pay_id', value: '결제 ID' },
                    { key: 'user_id', value: '회원 ID' },
                    { key: 'name', value: '이름' },
                    { key: 'nick_name', value: '닉네임' },
                    { key: 'phone_no', value: '연락처' },
                    { key: 'gathering_name', value: '모임명' },
                    { key: 'payment_amount', value: '결제 금액' },
                    { key: 'pay_type', value: '결제 수단' },
                    { key: 'pay_status', value: '결제 상태' },
                    { key: 'payment_date', value: '결제/신청일' },
                    { key: 'start_date', value: '시작일' },
                    { key: 'end_date', value: '종료일' },
                    { key: 'membership_state', value: '상태' },
                ]}
                tbodyRenderItem={(key, value, item) => {
                    if (key === 'pay_id')
                        return (
                            <button
                                type="button"
                                onClick={(event) => openPayment(event, item.pay_id)}
                                style={{ border: 0, padding: 0, background: 'none', color: '#426ff7', textDecoration: 'underline', cursor: 'pointer' }}
                            >
                                {value}
                            </button>
                        );
                    if (key === 'nick_name')
                        return (
                            <button
                                type="button"
                                onClick={(event) => openUser(event, item.user_id)}
                                style={{ border: 0, padding: 0, background: 'none', color: '#426ff7', textDecoration: 'underline', cursor: 'pointer' }}
                            >
                                {value}
                            </button>
                        );
                    if (key === 'gathering_name')
                        return (
                            <a
                                href={membershipAdminLinks(item, process.env.PUBLIC_URL || '').gathering}
                                onClick={(event) => event.stopPropagation()}
                                style={{ color: '#426ff7', textDecoration: 'underline' }}
                            >
                                {value}
                            </a>
                        );
                    if (key === 'payment_amount') return moneyFilter(value, '원');
                    if (key === 'pay_type') return methodLabel[value] || value;
                    if (key === 'pay_status') return paymentStatusLabel[value] || value;
                    if (key === 'membership_state') return membershipStateLabel(value);
                    if (key.includes('_at') && value) return dateFilter(value);
                    return value;
                }}
            />
            {selectedPayment && (
                <DefaultModal
                    visible
                    title={`${selectedPayment.data.name || ''} 회원님의 결제 상세 정보`}
                    xIcon
                    closeOn
                    onClickClose={() => setSelectedPayment(null)}
                    onClickBottomClose={() => setSelectedPayment(null)}
                    boxStyle={{ width: '100vh', height: '70vh' }}
                >
                    <PayInfoModal
                        data={selectedPayment.data}
                        originData={selectedPayment.origin}
                        onChange={(value, key) =>
                            setSelectedPayment((current) => ({
                                ...current,
                                data: { ...current.data, [key]: value },
                            }))
                        }
                    />
                </DefaultModal>
            )}
            {selected && (
                <DefaultModal
                    visible
                    title={`${selected.name || ''} 회원 멤버십 상세`}
                    xIcon
                    closeOn
                    onClickClose={() => setSelected(null)}
                    onClickBottomClose={() => setSelected(null)}
                    boxStyle={{ width: '620px', maxHeight: '80vh' }}
                >
                    <div style={{ padding: 24, display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
                        {[
                            ['결제 ID', selected.pay_id],
                            ['회원', `${selected.name || '-'} / ${selected.nick_name || '-'} (${selected.user_id})`],
                            ['연락처', selected.phone_no],
                            ['모임', `${selected.gathering_name} (${selected.gathering_id})`],
                            ['결제 수단', methodLabel[selected.pay_type] || selected.pay_type],
                            ['결제 금액', moneyFilter(selected.payment_amount, '원')],
                            ['결제 상태', paymentStatusLabel[selected.pay_status] || selected.pay_status],
                            ['결제/신청일', selected.payment_date ? dateFilter(selected.payment_date) : '-'],
                            ['멤버십 기간', `${selected.start_date || '-'} ~ ${selected.end_date || '-'}`],
                            ['상태', membershipStateLabel(selected.membership_state)],
                            ['환불 요청일', selected.refund_requested_at ? dateFilter(selected.refund_requested_at) : '-'],
                            ['환불 완료일', selected.refunded_at ? dateFilter(selected.refunded_at) : '-'],
                            ['환불 금액', moneyFilter(selected.refund_amount || 0, '원')],
                            ['환불 사유', selected.refund_remark || selected.end_reason || '-'],
                        ].map(([label, value]) => (
                            <React.Fragment key={label}>
                                <strong>{label}</strong>
                                <span>{value ?? '-'}</span>
                            </React.Fragment>
                        ))}
                    </div>
                </DefaultModal>
            )}
        </Wrapper02>
    );
};

export default Membership;
